/**
 * ============================================================================
 *  File        : frontend/src/pages/CasePages.tsx
 *  Module      : pages
 *  Purpose     : 案件子頁殼 — sub-nav + 施工中 placeholder（spec §8.2 /cases/:id/...）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import type { ReactElement } from 'react'
import { NavLink, Outlet, useParams } from 'react-router-dom'
import { caseRoutes, type CaseRouteDef } from '../data/caseRoutes'
import { usePermissions } from '../hooks/usePermissions'
import { cn } from '../lib/cn'
import { UnderConstruction } from './UnderConstruction'

interface CasePageProps {
  t: (key: string) => string
}

/** 案件工作區外殼：頂部子頁 tab + 巢狀 Outlet。無權限的 tab 隱藏。 */
export function CaseLayout({ t }: CasePageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const { can } = usePermissions()
  const visibleRoutes = caseRoutes.filter((route) => can(route.permissionKey))

  return (
    <section className="page-stack">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('case.workspace')}</span>
          <h1>
            {t('field.caseNo')} {caseId}
          </h1>
          <p>{t('desc.cases')}</p>
        </div>
      </header>
      <nav className="flex flex-wrap gap-1 border-b border-border pb-2" aria-label={t('case.modules')}>
        {visibleRoutes.map((route) => (
          <NavLink
            key={route.path || 'overview'}
            end={route.path === ''}
            to={route.path === '' ? `/cases/${caseId}` : `/cases/${caseId}/${route.path}`}
            className={({ isActive }) =>
              cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            {t(route.titleKey)}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </section>
  )
}

/** 依 caseRoutes 定義渲染單一案件子頁的施工中畫面。 */
export function CaseScreen({ def, t }: { def: CaseRouteDef; t: (key: string) => string }): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ':id'
  const route = def.path === '' ? `/cases/${caseId}` : `/cases/${caseId}/${def.path}`
  return (
    <UnderConstruction
      title={t(def.titleKey)}
      route={route}
      specSection={def.specSection}
      underConstructionLabel={
        def.status === 'blocked' ? t('status.blocked') : t('common.underConstruction')
      }
      description={
        def.status === 'blocked' ? t('case.stage.p3Desc') : t('common.underConstructionDesc')
      }
    />
  )
}
